/**
 * QuickSight Dashboard with AI Summary
 *
 * This component embeds a QuickSight dashboard and provides AI-powered
 * analysis of Action Plan Impact data. The AI button only appears when
 * the dashboard contains an Action Plan sheet.
 */

import * as React from 'react';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';
import { just } from 'maybeasy';
import { findPayload } from '@execonline-inc/collections';
import VisualizationEmbed from '../../Visualization/VisualizationEmbed';
import DashboardSummaryStore from '../../../DashboardSummaryStore';
import { T } from '../../../Translations';
import AIAnalysisDrawer from '../AIAnalysisDrawer';
import { QuickSightDashboardWithSummaryProps } from './types';
import { useQuickSightSummary } from './useQuickSightSummary';
import { loadDateFilter } from '../../DashboardSummary/utils';

const QuickSightDashboardWithSummary: React.FC<QuickSightDashboardWithSummaryProps> = ({
  currentDashboardId,
  dashboards,
  reportResource,
  onAIButtonStateChange,
}) => {
  const [summaryStore] = useState(() => new DashboardSummaryStore());
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Track whether we've already auto-opened the drawer for this dashboard load
  // This ensures auto-open is a ONE-TIME action that doesn't interfere with manual control
  const hasAutoOpenedRef = useRef(false);

  // Load saved date filter from Home page (for syncing dates to QuickSight)
  const savedFilter = useMemo(() => loadDateFilter(), []);

  // Build QuickSight parameters from saved date filter (from Home page)
  // If user came from Home page with a date filter, sync those dates to QuickSight
  // QuickSight expects dates in ISO 8601 format: yyyy-MM-ddTHH:mm:ss.SSSZ
  const quickSightParameters = useMemo(() => {
    if (!savedFilter) return undefined;

    // Convert YYYY-MM-DD to full ISO timestamp for QuickSight
    // Start date: beginning of day (00:00:00.000Z)
    // End date: end of day (23:59:59.999Z)
    const formatDateForQuickSight = (dateStr: string, isEndDate: boolean): string => {
      if (isEndDate) {
        return `${dateStr}T23:59:59.999Z`;
      }
      return `${dateStr}T00:00:00.000Z`;
    };

    return [
      { Name: 'pDateStart', Values: [formatDateForQuickSight(savedFilter.startDate, false)] },
      { Name: 'pDateEnd', Values: [formatDateForQuickSight(savedFilter.endDate, true)] },
    ];
  }, [savedFilter]);

  // Note: We don't pass initialFilters to useQuickSightSummary anymore.
  // Reports page waits for QuickSight to provide its actual parameters.
  // This ensures the API call uses the same dates that QuickSight is displaying.

  const {
    hasActionPlanSheet,
    handleDashboardReady,
    handleRefreshSummary,
    resetForDashboardChange,
    cleanup,
  } = useQuickSightSummary({
    currentDashboardId,
    dashboards,
    reportResource,
    summaryStore,
  });

  // Notify parent about AI button state changes so it can render the button in header
  useEffect(() => {
    if (onAIButtonStateChange) {
      onAIButtonStateChange({
        hasActionPlanSheet,
        openDrawer: () => setDrawerOpen(true),
      });
    }
  }, [hasActionPlanSheet, onAIButtonStateChange]);

  // Reset this component's own state when dashboard changes (adjust during render instead of effect)
  const prevDashboardIdRef = React.useRef(currentDashboardId);
  if (prevDashboardIdRef.current !== currentDashboardId) {
    prevDashboardIdRef.current = currentDashboardId;
    hasAutoOpenedRef.current = false; // Reset so auto-open can trigger for new dashboard
    setDrawerOpen(false); // Close drawer when switching dashboards
  }

  // The summary store is MobX state this observer reads, so resetting it during render would
  // schedule an update from inside the render. It is reset after commit instead, which is still
  // before the new dashboard's onDashboardReady, since that only fires once the embed has loaded.
  const resetDashboardIdRef = useRef(currentDashboardId);
  useEffect(() => {
    if (resetDashboardIdRef.current === currentDashboardId) return;
    resetDashboardIdRef.current = currentDashboardId;
    resetForDashboardChange();
  }, [currentDashboardId, resetForDashboardChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Auto-open drawer ONCE when data loads successfully
  // Uses MobX reaction because MobX observables don't trigger useEffect dependencies
  // The hasAutoOpenedRef ensures this is a one-time action that doesn't interfere with manual control
  useEffect(() => {
    const dispose = reaction(
      () => summaryStore.state.kind,
      (kind) => {
        if (kind === 'success' && !hasAutoOpenedRef.current) {
          hasAutoOpenedRef.current = true;
          setDrawerOpen(true);
        }
      },
    );
    return dispose;
  }, [summaryStore]);

  // Get summary state from store
  const getSummaryData = useCallback(() => {
    const state = summaryStore.state;
    if (state.kind === 'success') {
      return state.summary;
    }
    return null;
  }, [summaryStore.state]);

  const isLoading = summaryStore.state.kind === 'loading';
  const error = summaryStore.state.kind === 'failed' ? summaryStore.state.error : null;
  const emptyMessage = summaryStore.state.kind === 'empty' ? summaryStore.state.message : null;
  const summaryData = getSummaryData();

  return just({})
    .assign('dashboard', findPayload(currentDashboardId, dashboards))
    .map(({ dashboard }) => (
      <div>
        {/* AI Analysis Drawer - always mounted, visibility controlled by open prop */}
        <AIAnalysisDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          summary={summaryData}
          isLoading={isLoading}
          error={error}
          emptyMessage={emptyMessage}
          onRefresh={handleRefreshSummary}
        />

        {/* QuickSight Dashboard Embed */}
        <VisualizationEmbed
          visualizationPath={dashboard.payload.dashboardUri}
          width={`${dashboard.payload.width}px`}
          height={`${dashboard.payload.height}px`}
          name={dashboard.payload.name}
          dashboardType="quicksight"
          onReady={() => {}}
          onDashboardReady={handleDashboardReady}
          parameters={quickSightParameters}
        />
      </div>
    ))
    .getOrElse(() => (
      <p className="py-2.5">
        <T kind="Dashboard not found" />
      </p>
    ));
};

export default observer(QuickSightDashboardWithSummary);
